/**
 * Sidebar P1 (2026-05-20): smart attention 3-state group at the top of
 * the sidebar. Aggregates the same counts that drive per-item badges
 * (useNavCounts) into a single triage surface.
 *
 * Three states:
 *   - calm        — all counts zero. Group hides entirely.
 *   - background  — sum 1-5 AND no urgent item. Group collapsed with
 *                   total count badge.
 *   - active      — any urgent item (overdue bills) OR sum > 5. Group
 *                   expanded with red dot indicator.
 *
 * Items rendered inside are NOT new routes; each row deep-links to the
 * canonical route that already exists in the nav (with its own per-item
 * badge in the regular group). The attention group is a triage shortcut,
 * not a replacement.
 *
 * Pure module so the 3-state logic can be tested without rendering
 * React. SideNav.tsx wraps the return value to actually render.
 */

import type { ComponentType } from 'react';
import {
  ClipboardCheck, Users, FileStack,
  Shield, FileText, UserCog, Sparkles, ScanLine,
} from 'lucide-react';
import type { NavCounts } from '@/hooks/useNavCounts';
import type { UserRole } from '@/types';

/** Sprint 1.5 Layer 2 (2026-05-28). Threshold at which the AI budget row
 *  surfaces in Smart Attention. Same number across the 4-layer alert system:
 *  Layer 1 bar color, Layer 2 attention row, Layer 3 in-chat banner, Layer 4
 *  email cron. */
export const AI_BUDGET_WARN_PCT = 75;
/** ≥ this = urgent variant + red badge. */
export const AI_BUDGET_URGENT_PCT = 90;

/** Phase 7c (2026-07-12). Textract scanned-statement OCR page thresholds.
 *  Page-count based (not %) per operator: warn ~270, at-cap 300. Unlike the AI
 *  budget, NOTHING blocks — pages beyond the allowance accrue billable overage;
 *  the row is a heads-up, not a gate. Same 4-layer pattern (banner + Smart
 *  Attention + nav-counts + email cron), all using the SAME runway relationship:
 *  warn once within TEXTRACT_WARN_RUNWAY pages of the included allowance, so the
 *  three layers agree for ANY per-tier allowance (not just the 300 default). */
export const TEXTRACT_INCLUDED_PAGES = 300;
export const TEXTRACT_WARN_RUNWAY = 30;         // warn when (included - used) <= 30
export const TEXTRACT_WARN_PAGES = TEXTRACT_INCLUDED_PAGES - TEXTRACT_WARN_RUNWAY; // 270 for the 300 default
/** Warn floor for a specific included allowance (30-page runway before the cap). */
export function textractWarnFloor(included: number): number {
  return Math.max(1, (Number.isFinite(included) && included > 0 ? included : TEXTRACT_INCLUDED_PAGES) - TEXTRACT_WARN_RUNWAY);
}

// Role hierarchy MUST stay in sync with src/hooks/useRole.ts ROLE_LEVEL.
// Duplicated here (not imported) so this module stays pure + testable
// without pulling in React store + auth context.
const ROLE_LEVEL: Record<UserRole, number> = {
  platform_admin: 100,
  owner: 80,
  admin: 60,
  bookkeeper: 40,
  accountant: 30,
  viewer: 10,
  manager: 5,
  employee: 1,
};

export type AttentionState = 'calm' | 'background' | 'active';

export interface AttentionItem {
  to: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  count: number;
  variant: 'info' | 'warn' | 'urgent';
  // Sidebar P2 role-filter (2026-05-21): per-item minimum role required
  // to see this attention row. Matches the BACKEND role-gate on the
  // deep-linked route. If the user's level < ROLE_LEVEL[minRole], the
  // row is dropped from the attention group.
  //
  // Important: manager (level 5) sits BELOW viewer (level 10) by design,
  // so a minRole of 'viewer' correctly hides the row from pure managers
  // (who have their own /manager/* portal) and employees (who redirect
  // to /employee). See CLAUDE.md S35 + NS4-E2E lessons.
  minRole: UserRole;
  /** Sprint 1.5 Layer 2 (2026-05-28). When true, this row's `count` is a
   *  signal (e.g., AI budget percentage), NOT a quantity of pending things.
   *  Excluded from the total badge sum + from the `> ATTENTION_ACTIVE_SUM_THRESHOLD`
   *  active check (the row's own urgent/warn variant carries the escalation
   *  signal instead). Default: false. */
  excludeFromTotal?: boolean;
  /** Home Redesign Step 1 (2026-06-17). When true, this row ALSO renders as a
   *  card on the CommandCenter Home (`/`). Set ONLY on attention categories that
   *  have NO other Home surface today — missing-W9, policies-review,
   *  onboarding-unstarted, AI-budget. Categories already shown as Home
   *  vitals/lanes (overdue bills, compliance, approvals, PTO) leave this false
   *  to avoid double-surfacing. This is what stops the nav-pill collapse (Step 2)
   *  from stranding those alerts. Consumed via selectHomeAttention(). */
  homeCard?: boolean;
}

export interface AttentionRender {
  state: AttentionState;
  total: number;
  hasUrgent: boolean;
  items: AttentionItem[];
}

// The threshold above which the background state escalates to active
// even without any explicitly-urgent item. Tunable.
export const ATTENTION_ACTIVE_SUM_THRESHOLD = 5;

/**
 * Compute the attention render state from raw nav counts.
 *
 * @param counts — the value from useNavCounts()
 * @param role — optional caller role. When provided, rows whose minRole
 *               is above the caller's level are filtered out. When omitted
 *               (back-compat default), all rows are visible (legacy
 *               behavior; new callers should always pass role).
 * @returns the 3-state classification + per-item list (only items with
 *          count > 0 AND role-permitted are included; an attention group
 *          with state='calm' has an empty items[] array)
 */
export function computeAttentionState(counts: NavCounts, role?: UserRole): AttentionRender {
  // Build per-row item list, conditionally including only nonzero counts
  // AND role-permitted rows. Order matters: most-urgent shapes first.
  // Renderer iterates in order.
  //
  // Per-item minRole rationale (must match the BACKEND role-gate on each
  // linked route — verified against S40 role-gate hardening):
  //   - /finance/bills GET = viewer+ (ensureFinanceReadAccess)
  //   - /workflows = bookkeeper+ (approval workflow surface)
  //   - /hr = bookkeeper+ (PTO admin + employee management)
  //   - /contractors = bookkeeper+ (1099 + W-9 admin)
  //   - /compliance = viewer+ (calendar is read-only to all)
  //   - /documents = viewer+ (document vault is read-only to all)
  //   - /hr (onboarding) = bookkeeper+ (same as /hr above)
  //
  // Manager + employee roles are below viewer by design (see useRole.ts);
  // both will see EMPTY attention group regardless of counts. That's
  // correct — manager has /manager/approvals as its triage surface,
  // employee redirects to /employee/* portal.
  const allRows: AttentionItem[] = [
    {
      to: '/finance/bills',
      icon: FileStack,
      label: 'Overdue bills',
      count: counts.overdueBills,
      variant: 'urgent',
      minRole: 'viewer',
    },
    {
      to: '/workflows',
      icon: ClipboardCheck,
      label: 'Pending approvals',
      count: counts.pendingApprovals,
      variant: 'warn',
      minRole: 'bookkeeper',
    },
    {
      to: '/hr',
      icon: Users,
      label: 'Pending PTO requests',
      count: counts.pendingPto,
      variant: 'warn',
      minRole: 'bookkeeper',
    },
    {
      to: '/contractors',
      icon: Users,
      label: 'Missing W-9s',
      count: counts.missingW9,
      variant: 'warn',
      minRole: 'bookkeeper',
      homeCard: true,
    },
    {
      to: '/compliance',
      icon: Shield,
      label: 'Compliance due soon',
      count: counts.complianceDueSoon,
      variant: 'info',
      minRole: 'viewer',
    },
    {
      to: '/documents',
      icon: FileText,
      label: 'Policies need review',
      count: counts.policiesReviewNeeded ?? 0,
      variant: 'info',
      minRole: 'viewer',
      homeCard: true,
    },
    {
      to: '/hr',
      icon: UserCog,
      label: 'Onboarding unstarted',
      count: counts.onboardingUnstarted ?? 0,
      variant: 'info',
      minRole: 'bookkeeper',
      homeCard: true,
    },
  ];

  // Sprint 1.5 Layer 2 (2026-05-28). AI budget row is special: count = %,
  // not a quantity. Variant escalates on its own threshold (75 → warn,
  // 90 → urgent). Only included when ≥75% so the row stays out of
  // calm/background until there's real signal. minRole='admin' because
  // budget signals are owner/admin financial concerns — bookkeepers + below
  // can't change the cap from the UI (Settings → Billing is owner+).
  const aiBudgetPct = counts.aiBudgetPct ?? 0;
  if (aiBudgetPct >= AI_BUDGET_WARN_PCT) {
    allRows.push({
      to: '/settings/ai-usage',
      icon: Sparkles,
      label: `AI budget at ${aiBudgetPct}%`,
      count: aiBudgetPct,
      variant: aiBudgetPct >= AI_BUDGET_URGENT_PCT ? 'urgent' : 'warn',
      minRole: 'admin',
      excludeFromTotal: true,
      homeCard: true,
    });
  }

  // Phase 7c (2026-07-12). Statement OCR page usage. Like the AI budget row, the
  // `count` is a signal (pages used, not a quantity of actionable items), so it's
  // excludeFromTotal. Warn at ≥270, "urgent" once past the included allowance (300
  // default; the real per-tier allowance flows through textractPagesIncluded). The
  // urgent variant here means "billing overage is accruing," NOT "blocked" — uploads
  // continue. minRole='admin' (card statements are an owner/admin money surface).
  const textractUsed = counts.textractPagesUsed ?? 0;
  const textractIncl = counts.textractPagesIncluded ?? TEXTRACT_INCLUDED_PAGES;
  if (textractUsed >= textractWarnFloor(textractIncl)) {
    allRows.push({
      to: '/finance/card-statements',
      icon: ScanLine,
      label: `Statement OCR ${textractUsed}/${textractIncl} pages`,
      count: textractUsed,
      variant: textractUsed >= textractIncl ? 'urgent' : 'warn',
      minRole: 'admin',
      excludeFromTotal: true,
      homeCard: true,
    });
  }

  // Filter: nonzero count AND role-permitted (if role provided).
  // Role omission = legacy behavior (all rows pass the role check).
  const callerLevel = role ? (ROLE_LEVEL[role] ?? 0) : Infinity;
  const items = allRows.filter(r => {
    if (r.count <= 0) return false;
    if (callerLevel < ROLE_LEVEL[r.minRole]) return false;
    return true;
  });
  // Total sums only ROWS that represent pending quantities (excludes the AI
  // budget row whose `count` is a percentage signal, not a count of things).
  const total = items
    .filter(r => !r.excludeFromTotal)
    .reduce((sum, r) => sum + r.count, 0);
  const hasUrgent = items.some(r => r.variant === 'urgent' && r.count > 0);

  // State derivation. Sprint 1.5 (2026-05-28) tightened the calm check from
  // `total === 0` to `items.length === 0` so a row whose count is excluded
  // from total (e.g., AI budget %) can still trigger background/active state.
  let state: AttentionState;
  if (items.length === 0) {
    state = 'calm';
  } else if (hasUrgent || total > ATTENTION_ACTIVE_SUM_THRESHOLD) {
    state = 'active';
  } else {
    state = 'background';
  }

  return { state, total, hasUrgent, items };
}

/**
 * Home Redesign Step 1 (2026-06-17). The attention rows that should ALSO render
 * as cards on the CommandCenter Home (`/`) — those flagged homeCard, i.e. the
 * categories with no other Home surface (missing-W9, policies-review,
 * onboarding-unstarted, AI-budget). Pass the already role+count-filtered
 * `render.items` from computeAttentionState so Home inherits the same role
 * gating as the sidebar. Pure for testability.
 */
export function selectHomeAttention(items: AttentionItem[]): AttentionItem[] {
  return items.filter((i) => i.homeCard);
}

// (No icon re-exports — consumers import AlertTriangle directly from
// lucide-react alongside other icons they use. Re-exporting one icon
// here just to save one import line in SideNav would invite future
// drift if either side changes the icon.)
